"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { AiAssistNotice } from "@/components/ai-assist-notice";
import { Badge, Button, Card, EmptyState, FieldLabel, Modal, SelectInput, TextArea } from "@/components/ui";
import { classifyEquipmentDemoImage, confirmEquipmentDemoSubmission, loadEquipmentDemoCatalog, uploadEquipmentDemoChecklistPhoto } from "@/lib/api";
import { AI_ASSIST_NOTICES } from "@/lib/legal-notices";
import type { BadgeTone } from "@/components/ui";
import type {
  EquipmentChecklistItemPhoto,
  EquipmentDemoCatalog,
  EquipmentDemoSubmission,
  EquipmentPhotoRequirement,
  EquipmentPhotoStatus,
  MachineChecklist,
  MachineDocumentEntry,
  MachineChecklistExampleImage,
  MachineClassification,
  MachineClassificationCandidate,
  MachineChecklistItem,
  MachineReferenceMaterial,
  MachineStatus,
  ReferenceTableRow,
} from "@/lib/types";

const steps = [
  { key: "capture", label: "촬영" },
  { key: "review", label: "인식" },
  { key: "action", label: "선택" },
  { key: "reference", label: "참고" },
  { key: "checklist", label: "점검" },
  { key: "summary", label: "저장" },
] as const;

type StepKey = (typeof steps)[number]["key"];
type CaptureSource = "camera" | "upload";
type SelectionSource = "llm_confirmed" | "candidate" | "dropdown";
type LocalChecklistItemPhoto = EquipmentChecklistItemPhoto & { preview_url?: string };

const photoRequirementValues = new Set(["required", "optional", "skippable", "not_required"]);
const checklistPhotoMaxSide = 1600;
const checklistPhotoJpegQuality = 0.75;

function buildEmptyCatalog(): EquipmentDemoCatalog {
  return {
    machine_count: 0,
    machines: [],
    supported_uploads: [],
    classification_backend: "",
    environment_issues: [],
  };
}

function buildEmptyClassification(): MachineClassification {
  return {
    status: "error",
    equipment_type: null,
    confidence: "low",
    matched_label: null,
    reasoning_summary: "사진을 촬영하면 AI 인식 결과가 여기에 표시됩니다.",
    visible_evidence: [],
    candidates: [],
    top_candidate: null,
    error_message: null,
  };
}

function confidenceTone(confidence: string): BadgeTone {
  if (confidence === "high") return "emerald";
  if (confidence === "medium") return "amber";
  return "ink";
}

function confidenceLabel(confidence: string): string {
  if (confidence === "high") return "높음";
  if (confidence === "medium") return "보통";
  if (confidence === "low") return "낮음";
  return confidence || "-";
}

function classificationStatusLabel(status: string): string {
  if (status === "supported") return "후보 찾음";
  if (status === "unsupported") return "적합한 장비 없음";
  if (status === "error") return "인식 실패";
  return status || "-";
}

function statusTone(status: MachineStatus): BadgeTone {
  if (status === "적합") return "emerald";
  if (status === "부적합") return "rose";
  if (status === "직접기입") return "teal";
  if (status === "해당없음") return "ink";
  return "amber";
}

function statusLabel(status: MachineStatus): string {
  return status === "미확인" ? "보류" : status;
}

function exampleImageTone(kind: MachineChecklistExampleImage["kind"]): BadgeTone {
  return kind === "bad" ? "rose" : "emerald";
}

function exampleImagesForItem(item: MachineChecklistItem): MachineChecklistExampleImage[] {
  return item.photo_examples?.length ? item.photo_examples : item.example_images ?? [];
}

function isAiGeneratedExample(image: MachineChecklistExampleImage): boolean {
  return image.source_kind === "ai_generated" || image.source_kind === "ai_generated_suggestion";
}

function documentStatusTone(status: MachineDocumentEntry["status"]): BadgeTone {
  return status === "available" ? "teal" : "amber";
}

function documentGroupButtonTitle(title: string): string {
  return title.replace(/^\d+\.\s*/, "").trim() || title;
}

function isChecklistReferenceDocument(document: MachineDocumentEntry): boolean {
  return document.document_type === "pre_entry_reference_road" || document.document_type === "pre_entry_reference_safety";
}

function isDailyChecklistDocument(document: MachineDocumentEntry): boolean {
  return document.document_type === "daily_equipment_checklist" || document.action === "start_checklist";
}

function firstChecklistReferenceMaterialId(checklist: MachineChecklist | null): string {
  if (!checklist) return "";
  return (
    checklist.document_groups
      ?.find((group) => group.group_id === "checklist")
      ?.documents.find(isChecklistReferenceDocument)
      ?.materials[0]?.material_id
    || checklist.reference_materials[0]?.material_id
    || ""
  );
}

function photoRequirement(item: MachineChecklistItem): EquipmentPhotoRequirement {
  const value = String(item.photo_requirement || "").toLowerCase();
  if (photoRequirementValues.has(value)) return value as EquipmentPhotoRequirement;
  return "not_required";
}

function acceptsChecklistPhoto(item: MachineChecklistItem): boolean {
  return photoRequirement(item) !== "not_required";
}

function requiresChecklistPhoto(item: MachineChecklistItem): boolean {
  return photoRequirement(item) === "required";
}

function photoRequirementLabel(item: MachineChecklistItem): string {
  const requirement = photoRequirement(item);
  if (requirement === "required") return "사진 권장";
  if (requirement === "optional") return "사진 선택";
  if (requirement === "skippable") return "건너뛰기 가능";
  return "사진 불필요";
}

function photoRequirementTone(item: MachineChecklistItem): BadgeTone {
  const requirement = photoRequirement(item);
  if (requirement === "required") return "teal";
  if (requirement === "optional") return "neutral";
  if (requirement === "skippable") return "amber";
  return "ink";
}

function photoStatusLabel(status: EquipmentPhotoStatus | undefined, hasPhoto = false): string {
  if (status === "submitted_late") return "보완 제출";
  if (status === "submitted" || hasPhoto) return "사진 있음";
  if (status === "skipped") return "건너뜀";
  if (status === "missing") return "미제출";
  return "사진 없음";
}

function photoStatusTone(status: EquipmentPhotoStatus | undefined, hasPhoto = false): BadgeTone {
  if (status === "submitted_late" || status === "submitted" || hasPhoto) return "teal";
  if (status === "skipped") return "neutral";
  if (status === "missing") return "amber";
  return "neutral";
}

function buildChecklistPhotoStatuses(
  checklist: MachineChecklist | null,
  photos: Record<string, LocalChecklistItemPhoto>,
  explicitStatuses: Record<string, EquipmentPhotoStatus>,
): Record<string, EquipmentPhotoStatus> {
  if (!checklist) return {};
  const result: Record<string, EquipmentPhotoStatus> = {};
  for (const item of checklist.items) {
    const itemId = item.item_id;
    const explicit = explicitStatuses[itemId];
    if (photos[itemId]) {
      result[itemId] = explicit === "submitted_late" ? "submitted_late" : "submitted";
    } else if (explicit === "skipped") {
      result[itemId] = "skipped";
    } else if (requiresChecklistPhoto(item)) {
      result[itemId] = "missing";
    } else if (explicit) {
      result[itemId] = explicit;
    }
  }
  return result;
}

function photoFileName(filename: string) {
  const base = filename.replace(/\.[^.]+$/, "") || "checklist-photo";
  return `${base}.jpg`;
}

async function compressChecklistImage(file: File): Promise<{ file: File; metadata: Record<string, unknown> }> {
  const metadata: Record<string, unknown> = {
    client_max_side: checklistPhotoMaxSide,
    client_jpeg_quality: checklistPhotoJpegQuality,
    original_size_bytes: file.size,
    original_type: file.type || "application/octet-stream",
  };
  if (!file.type.startsWith("image/")) {
    return { file, metadata: { ...metadata, compression_skipped: true, reason: "not_image_mime" } };
  }

  try {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(reader.error ?? new Error("이미지를 읽지 못했습니다."));
      reader.readAsDataURL(file);
    });
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("이미지를 해석하지 못했습니다."));
      img.src = dataUrl;
    });
    const longestSide = Math.max(image.naturalWidth, image.naturalHeight, 1);
    const scale = Math.min(1, checklistPhotoMaxSide / longestSide);
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("이미지 압축 컨텍스트를 만들지 못했습니다.");
    context.drawImage(image, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", checklistPhotoJpegQuality);
    });
    if (!blob) throw new Error("JPEG 압축에 실패했습니다.");
    const compressed = new File([blob], photoFileName(file.name), {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
    return {
      file: compressed,
      metadata: {
        ...metadata,
        client_width: width,
        client_height: height,
        original_width: image.naturalWidth,
        original_height: image.naturalHeight,
        compressed_size_bytes: compressed.size,
        compression_source: "browser_canvas",
      },
    };
  } catch (error) {
    return {
      file,
      metadata: {
        ...metadata,
        compression_failed: true,
        compression_error: error instanceof Error ? error.message : String(error),
      },
    };
  }
}

function stepIndex(step: StepKey): number {
  return steps.findIndex((item) => item.key === step);
}

function StepPills({ current }: { current: StepKey }) {
  const activeIndex = stepIndex(current);
  return (
    <div className="flex items-center gap-2 overflow-x-auto">
      {steps.map((step, index) => (
        <div
          key={step.key}
          className={[
            "flex min-w-fit items-center gap-2 rounded-full px-3 py-2 text-xs font-semibold sm:text-sm",
            index === activeIndex
              ? "bg-ink-900 text-white"
              : index < activeIndex
                ? "bg-emerald-100 text-emerald-800"
                : "bg-white text-ink-500 ring-1 ring-inset ring-ink-200",
          ].join(" ")}
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20 text-xs">{index + 1}</span>
          <span>{step.label}</span>
        </div>
      ))}
    </div>
  );
}

function ChecklistActionPanel({
  onAccept,
  onReject,
  onDirectInput,
  size = "default",
}: {
  onAccept: () => void;
  onReject: () => void;
  onDirectInput: () => void;
  size?: "default" | "compact";
}) {
  const primaryHeight = size === "compact" ? "min-h-[56px]" : "min-h-[64px]";
  const ghostHeight = size === "compact" ? "min-h-[40px]" : "min-h-[48px]";
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onAccept}
          className={`flex ${primaryHeight} items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 text-base font-bold text-white transition hover:bg-emerald-500 active:bg-emerald-700`}
        >
          <span aria-hidden>✓</span>
          <span>적합</span>
        </button>
        <button
          type="button"
          onClick={onReject}
          className={`flex ${primaryHeight} items-center justify-center gap-2 rounded-2xl bg-rose-600 px-4 text-base font-bold text-white transition hover:bg-rose-500 active:bg-rose-700`}
        >
          <span aria-hidden>✕</span>
          <span>부적합</span>
        </button>
      </div>
      <button
        type="button"
        onClick={onDirectInput}
        className={`flex w-full ${ghostHeight} items-center justify-center gap-2 rounded-2xl border border-ink-300 bg-white px-4 text-sm font-semibold text-ink-700 transition hover:bg-ink-50`}
      >
        <span aria-hidden>✎</span>
        <span>직접 기입</span>
      </button>
    </div>
  );
}

function ChecklistOverviewTable({
  items,
  activeIndex,
  statuses,
  photoStatuses,
  onJump,
  mode,
}: {
  items: MachineChecklistItem[];
  activeIndex: number;
  statuses: Record<string, MachineStatus>;
  photoStatuses: Record<string, EquipmentPhotoStatus>;
  onJump: (index: number) => void;
  mode: "sidebar" | "mobile-collapsible";
}) {
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const activeRowRef = useRef<HTMLTableRowElement | null>(null);

  const categoryInfo = useMemo(() => {
    const spans: Record<number, number> = {};
    const show: Record<number, boolean> = {};
    let runStart: number | null = null;
    let lastCategory: string | null = null;
    items.forEach((item, index) => {
      const cat = item.category || "";
      if (cat && cat === lastCategory && runStart !== null) {
        spans[runStart] = (spans[runStart] || 1) + 1;
        show[index] = false;
      } else {
        spans[index] = 1;
        show[index] = true;
        lastCategory = cat;
        runStart = cat ? index : null;
      }
    });
    return { spans, show };
  }, [items]);

  const completedCount = items.filter((item) => (statuses[item.item_id] || "미확인") !== "미확인").length;

  useEffect(() => {
    if (!activeRowRef.current) return;
    if (mode === "mobile-collapsible" && !mobileExpanded) return;
    activeRowRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeIndex, mode, mobileExpanded]);

  const visible = mode === "sidebar" || mobileExpanded;

  const headerSection =
    mode === "mobile-collapsible" ? (
      <button
        type="button"
        onClick={() => setMobileExpanded((current) => !current)}
        className="flex w-full items-center justify-between rounded-2xl bg-ink-50 px-4 py-3 text-sm font-semibold text-ink-800"
      >
        <span>전체 점검표 보기</span>
        <span className="flex items-center gap-2 text-xs font-bold text-ink-600">
          <span>{completedCount}/{items.length}</span>
          <span aria-hidden>{mobileExpanded ? "▲" : "▼"}</span>
        </span>
      </button>
    ) : (
      <div className="flex items-center justify-between border-b border-ink-100 px-3 py-2">
        <p className="text-xs font-bold text-ink-700">전체 점검표</p>
        <p className="text-xs font-semibold text-ink-500">{completedCount}/{items.length}</p>
      </div>
    );

  const tableJsx = (
    <table className="w-full border-collapse text-[11px]">
      <thead className="sticky top-0 bg-ink-50 text-ink-700">
        <tr>
          <th className="border border-ink-200 px-1 py-1 text-left">구분</th>
          <th className="w-8 border border-ink-200 px-1 py-1 text-center">NO</th>
          <th className="border border-ink-200 px-2 py-1 text-left">점검사항</th>
          <th className="w-16 border border-ink-200 px-1 py-1 text-center">결과</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, index) => {
          const isActive = index === activeIndex;
          const status = statuses[item.item_id] || "미확인";
          const showCategory = categoryInfo.show[index];
          const span = categoryInfo.spans[index] || 1;
          const photoStatus = photoStatuses[item.item_id];
          const rowClass = [
            "cursor-pointer align-top transition",
            isActive
              ? "bg-rose-50 text-rose-900"
              : status !== "미확인"
                ? "bg-emerald-50/30 hover:bg-emerald-50"
                : "hover:bg-ink-50",
          ].join(" ");
          return (
            <tr
              key={item.item_id}
              ref={isActive ? activeRowRef : undefined}
              onClick={() => onJump(index)}
              className={rowClass}
            >
              {showCategory ? (
                <td
                  rowSpan={span}
                  className="border border-ink-200 bg-ink-50/40 px-1 py-1 align-middle text-center text-[10px] font-semibold text-ink-700"
                >
                  {item.category || ""}
                </td>
              ) : null}
              <td
                className={[
                  "border border-ink-200 px-1 py-1 text-center font-bold",
                  isActive ? "border-l-4 border-l-rose-500" : "",
                ].join(" ")}
              >
                {item.number}
              </td>
              <td className="border border-ink-200 px-2 py-1 leading-5">
                <p className="line-clamp-2">{item.question}</p>
                {photoStatus && photoStatus !== "missing" ? (
                  <p className="mt-0.5 text-[10px] text-emerald-700">사진 {photoStatus === "submitted" ? "제출" : photoStatus === "skipped" ? "건너뜀" : photoStatus}</p>
                ) : null}
              </td>
              <td className="border border-ink-200 px-1 py-1 text-center">
                {status === "미확인" ? (
                  <span className="text-[10px] text-ink-400">-</span>
                ) : (
                  <Badge tone={statusTone(status)}>{statusLabel(status)}</Badge>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  if (mode === "mobile-collapsible") {
    return (
      <div className="space-y-2">
        {headerSection}
        {visible ? (
          <div className="max-h-72 overflow-y-auto overflow-x-auto rounded-2xl border border-ink-200 bg-white">
            {tableJsx}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-ink-200 bg-white">
      {headerSection}
      <div className="max-h-[calc(100vh-8rem)] overflow-y-auto overflow-x-auto">{tableJsx}</div>
    </div>
  );
}

type ReferenceResponse = { result: MachineStatus | ""; note: string };

const EMPTY_REFERENCE_RESPONSE: ReferenceResponse = { result: "", note: "" };

function ReferenceOverviewTable({
  rows,
  responses,
  activeIndex,
  onJump,
  mode,
}: {
  rows: ReferenceTableRow[];
  responses: Record<string, ReferenceResponse>;
  activeIndex: number;
  onJump: (index: number) => void;
  mode: "sidebar" | "mobile";
}) {
  const [expanded, setExpanded] = useState(true);
  const activeRowRef = useRef<HTMLTableRowElement | null>(null);

  const categoryInfo = useMemo(() => {
    const spans: Record<number, number> = {};
    const show: Record<number, boolean> = {};
    let runStart: number | null = null;
    let last: string | null = null;
    rows.forEach((row, index) => {
      const cat = row.category || "";
      if (cat && cat === last && runStart !== null) {
        spans[runStart] = (spans[runStart] || 1) + 1;
        show[index] = false;
      } else {
        spans[index] = 1;
        show[index] = true;
        last = cat;
        runStart = cat ? index : null;
      }
    });
    return { spans, show };
  }, [rows]);

  const completed = rows.filter((row) => Boolean(responses[row.row_id]?.result)).length;

  useEffect(() => {
    if (!activeRowRef.current) return;
    if (mode === "mobile" && !expanded) return;
    activeRowRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeIndex, mode, expanded]);

  const hasCategory = rows.some((row) => row.category);

  const tableJsx = (
    <table className="w-full border-collapse text-[11px]">
      <thead className="sticky top-0 bg-ink-50 text-ink-700">
        <tr>
          {hasCategory ? <th className="border border-ink-200 px-1 py-1 text-left">구분</th> : null}
          <th className="w-8 border border-ink-200 px-1 py-1 text-center">NO</th>
          <th className="border border-ink-200 px-2 py-1 text-left">점검사항</th>
          <th className="w-16 border border-ink-200 px-1 py-1 text-center">결과</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => {
          const isActive = index === activeIndex;
          const response = responses[row.row_id];
          const status = response?.result || "";
          const showCategory = categoryInfo.show[index];
          const span = categoryInfo.spans[index] || 1;
          return (
            <tr
              key={row.row_id}
              ref={isActive ? activeRowRef : undefined}
              onClick={() => onJump(index)}
              className={[
                "cursor-pointer align-top transition",
                isActive
                  ? "bg-rose-50 text-rose-900"
                  : status
                    ? "bg-emerald-50/30 hover:bg-emerald-50"
                    : "hover:bg-ink-50",
              ].join(" ")}
            >
              {hasCategory && showCategory ? (
                <td
                  rowSpan={span}
                  className="border border-ink-200 bg-ink-50/40 px-1 py-1 align-middle text-center text-[10px] font-semibold text-ink-700"
                >
                  {row.category || ""}
                </td>
              ) : null}
              <td
                className={[
                  "border border-ink-200 px-1 py-1 text-center font-bold",
                  isActive ? "border-l-4 border-l-rose-500" : "",
                ].join(" ")}
              >
                {row.number ?? ""}
              </td>
              <td className="border border-ink-200 px-2 py-1 leading-5">
                <p className="line-clamp-2">{row.question}</p>
              </td>
              <td className="border border-ink-200 px-1 py-1 text-center">
                {status ? <Badge tone={statusTone(status)}>{statusLabel(status)}</Badge> : <span className="text-[10px] text-ink-400">-</span>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  if (mode === "mobile") {
    return (
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="flex w-full items-center justify-between rounded-2xl bg-ink-50 px-4 py-2 text-sm font-semibold text-ink-800"
        >
          <span>전체 표 보기</span>
          <span className="flex items-center gap-2 text-xs font-bold text-ink-600">
            <span>
              {completed}/{rows.length}
            </span>
            <span aria-hidden>{expanded ? "▲" : "▼"}</span>
          </span>
        </button>
        {expanded ? (
          <div className="max-h-60 overflow-y-auto overflow-x-auto rounded-2xl border border-ink-200 bg-white">
            {tableJsx}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-ink-200 bg-white">
      <div className="flex items-center justify-between border-b border-ink-100 px-3 py-2">
        <p className="text-xs font-bold text-ink-700">전체 표</p>
        <p className="text-xs font-semibold text-ink-500">
          {completed}/{rows.length}
        </p>
      </div>
      <div className="max-h-[60vh] overflow-y-auto overflow-x-auto">{tableJsx}</div>
    </div>
  );
}

function ReferenceFlow({
  material,
  responses,
  onResponseChange,
}: {
  material: MachineReferenceMaterial & { inline_table: NonNullable<MachineReferenceMaterial["inline_table"]> };
  responses: Record<string, ReferenceResponse>;
  onResponseChange: (rowId: string, partial: Partial<ReferenceResponse>) => void;
}) {
  const rows = material.inline_table.rows;
  const [activeIndex, setActiveIndex] = useState(0);
  const noteRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    setActiveIndex(0);
  }, [material.material_id]);

  if (!rows.length) {
    return (
      <div className="rounded-2xl border border-dashed border-ink-200 bg-ink-50/60 p-6 text-center">
        <p className="text-sm text-ink-600">표에서 점검 항목을 추출하지 못했습니다.</p>
      </div>
    );
  }

  const safeIndex = Math.min(Math.max(activeIndex, 0), rows.length - 1);
  const currentRow = rows[safeIndex];
  const currentResponse = responses[currentRow.row_id] || EMPTY_REFERENCE_RESPONSE;

  function moveTo(index: number) {
    setActiveIndex(Math.min(Math.max(index, 0), rows.length - 1));
  }

  function applyResult(result: MachineStatus) {
    onResponseChange(currentRow.row_id, { result });
    if (result !== "직접기입" && safeIndex < rows.length - 1) {
      setActiveIndex(safeIndex + 1);
    }
    if (result === "직접기입") {
      window.setTimeout(() => noteRef.current?.focus(), 50);
    }
  }

  return (
    <div className="space-y-3 sm:grid sm:grid-cols-[240px_minmax(0,1fr)] sm:items-start sm:gap-3 sm:space-y-0">
      <aside className="hidden self-start sm:block">
        <ReferenceOverviewTable
          rows={rows}
          responses={responses}
          activeIndex={safeIndex}
          onJump={moveTo}
          mode="sidebar"
        />
      </aside>

      <div className="space-y-3">
        <div className="sm:hidden">
          <ReferenceOverviewTable
            rows={rows}
            responses={responses}
            activeIndex={safeIndex}
            onJump={moveTo}
            mode="mobile"
          />
        </div>

        <div className="rounded-3xl border border-ink-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
            {currentRow.category ? (
              <span className="rounded-full bg-ink-100 px-2 py-0.5 font-semibold text-ink-700">{currentRow.category}</span>
            ) : null}
            <span className="font-semibold">
              체크 항목 {currentRow.number ?? safeIndex + 1} · {safeIndex + 1}/{rows.length}
            </span>
            {currentResponse.result ? (
              <Badge tone={statusTone(currentResponse.result)}>{statusLabel(currentResponse.result)}</Badge>
            ) : null}
          </div>
          <p className="mt-3 whitespace-pre-wrap text-lg font-bold leading-7 text-ink-900">{currentRow.question}</p>
          {currentRow.result_hint ? <p className="mt-2 text-xs text-ink-500">{currentRow.result_hint}</p> : null}
          <div className="mt-4">
            <label className="text-xs font-semibold text-ink-700">비고{currentResponse.result === "직접기입" ? " (필수)" : " (선택)"}</label>
            <textarea
              ref={noteRef}
              value={currentResponse.note}
              onChange={(event) => onResponseChange(currentRow.row_id, { note: event.target.value })}
              placeholder={currentRow.note_hint || "비고가 있다면 입력해 주세요."}
              rows={3}
              className={[
                "mt-1 w-full rounded-xl border px-3 py-2 text-sm text-ink-900 focus:outline-none",
                currentResponse.result === "직접기입"
                  ? "border-ink-900 ring-1 ring-ink-200 focus:ring-ink-400"
                  : "border-ink-200 focus:ring-1 focus:ring-ink-300",
              ].join(" ")}
            />
          </div>
        </div>

        <ChecklistActionPanel
          onAccept={() => applyResult("적합")}
          onReject={() => applyResult("부적합")}
          onDirectInput={() => applyResult("직접기입")}
          size="compact"
        />

        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => moveTo(safeIndex - 1)}
            disabled={safeIndex === 0}
            className="rounded-2xl border border-ink-200 px-3 py-2 text-sm font-semibold text-ink-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            이전 항목
          </button>
          <button
            type="button"
            onClick={() => moveTo(safeIndex + 1)}
            disabled={safeIndex >= rows.length - 1}
            className="rounded-2xl border border-ink-200 px-3 py-2 text-sm font-semibold text-ink-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            다음 항목
          </button>
        </div>
      </div>
    </div>
  );
}

function ReferenceMaterialViewer({
  materials,
  activeMaterial,
  onSelectMaterial,
  responses,
  onResponseChange,
  onDownload,
  downloadInProgress,
}: {
  materials: MachineReferenceMaterial[];
  activeMaterial: MachineReferenceMaterial | null;
  onSelectMaterial: (materialId: string) => void;
  responses: Record<string, ReferenceResponse>;
  onResponseChange: (rowId: string, partial: Partial<ReferenceResponse>) => void;
  onDownload: () => Promise<void> | void;
  downloadInProgress: boolean;
}) {
  if (!materials.length) {
    return (
      <EmptyState
        title="참고자료가 없습니다"
        description="선택한 장비에 연결된 참고자료가 아직 등록되지 않았습니다."
      />
    );
  }

  const inlineTable = activeMaterial?.inline_table || null;
  const filledRowCount = inlineTable
    ? inlineTable.rows.filter((row) => {
        const entry = responses[row.row_id];
        return Boolean(entry?.result || entry?.note);
      }).length
    : 0;
  const canDownload = Boolean(inlineTable) && filledRowCount > 0 && Boolean(activeMaterial?.hwpx_source_path);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        {materials.map((material) => {
          const active = material.material_id === (activeMaterial?.material_id || "");
          return (
            <button
              key={material.material_id}
              type="button"
              onClick={() => onSelectMaterial(material.material_id)}
              className={[
                "rounded-2xl px-4 py-2 text-sm font-semibold transition",
                active ? "bg-ink-900 text-white" : "bg-white text-ink-700 ring-1 ring-inset ring-ink-200 hover:bg-ink-50",
              ].join(" ")}
            >
              {material.title}
            </button>
          );
        })}
      </div>

      {activeMaterial ? (
        <div className="space-y-4">
          <div className="rounded-3xl bg-ink-50 p-4">
            <p className="text-sm font-semibold text-ink-900">{activeMaterial.title}</p>
            <p className="mt-1 text-sm text-ink-500">
              {activeMaterial.source_label ? `${activeMaterial.source_label} 자료` : activeMaterial.pdf_source_path ? "PDF 자료" : "참고자료"}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {activeMaterial.pdf_source_path ? (
                <a
                  href={`/api-proxy/v1/equipment-demo/reference-materials/${encodeURIComponent(activeMaterial.material_id)}/file?kind=pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-ink-700 ring-1 ring-inset ring-ink-200 transition hover:bg-ink-100"
                >
                  PDF 원본 보기
                </a>
              ) : null}
            </div>
          </div>

          {inlineTable ? (
            <ReferenceFlow
              material={{ ...activeMaterial, inline_table: inlineTable }}
              responses={responses}
              onResponseChange={onResponseChange}
            />
          ) : null}

          {inlineTable ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-ink-200 bg-white p-4">
              <p className="text-sm text-ink-600">
                {filledRowCount > 0
                  ? `입력된 항목 ${filledRowCount}개를 HWPX 양식에 채워 내려받을 수 있습니다.`
                  : "한 행 이상 점검 결과를 입력하면 HWPX 다운로드가 활성화됩니다."}
              </p>
              <button
                type="button"
                onClick={() => {
                  void onDownload();
                }}
                disabled={!canDownload || downloadInProgress}
                className={[
                  "rounded-2xl px-4 py-2 text-sm font-semibold transition",
                  canDownload && !downloadInProgress
                    ? "bg-ink-900 text-white hover:bg-ink-700"
                    : "bg-ink-200 text-ink-500 cursor-not-allowed",
                ].join(" ")}
              >
                {downloadInProgress ? "생성 중..." : "점검 결과 HWPX 다운로드"}
              </button>
            </div>
          ) : null}

          {activeMaterial.preview_pages.length > 0 ? (
            <div className="space-y-4">
              <div className="rounded-3xl border border-ink-200 bg-ink-50 p-4 text-sm leading-7 text-ink-700">
                PDF에서 사진이 들어있는 페이지를 발췌해 웹에서 보기 쉽게 정리했습니다.
              </div>
              {activeMaterial.preview_pages.map((preview) => (
                <div key={`${activeMaterial.material_id}-${preview.page_number}`} className="overflow-hidden rounded-3xl border border-ink-200 bg-white">
                  <div className="border-b border-ink-100 px-4 py-3">
                    <p className="text-sm font-semibold text-ink-900">페이지 {preview.page_number}</p>
                  </div>
                  <img
                    src={`/api-proxy/v1/equipment-demo/reference-materials/${encodeURIComponent(activeMaterial.material_id)}/preview/${preview.page_number}`}
                    alt={`${activeMaterial.title} 참고자료 페이지 ${preview.page_number}`}
                    className="w-full bg-white object-contain"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-3xl border border-dashed border-ink-200 bg-ink-50/60 p-8 text-center">
              <p className="text-base font-bold text-ink-900">웹 미리보기용 페이지를 찾지 못했습니다</p>
              <p className="mx-auto mt-2 max-w-xl text-sm leading-7 text-ink-600">
                PDF에 이미지가 없거나 미리보기로 추출할 수 없는 형식입니다. 원본 자료 등록 상태를 확인해 주세요.
              </p>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function EquipmentDemoPage({
  distributionToken = "",
  sharedAccess = false,
}: {
  distributionToken?: string;
  sharedAccess?: boolean;
}) {
  const router = useRouter();
  const [catalog, setCatalog] = useState<EquipmentDemoCatalog>(buildEmptyCatalog());
  const [currentStep, setCurrentStep] = useState<StepKey>("capture");
  const [classification, setClassification] = useState<MachineClassification>(buildEmptyClassification());
  const [draftId, setDraftId] = useState("");
  const [fileName, setFileName] = useState("");
  const [captureSource, setCaptureSource] = useState<CaptureSource>("camera");
  const [selectedImagePreviewUrl, setSelectedImagePreviewUrl] = useState("");
  const [selectedMachineId, setSelectedMachineId] = useState("");
  const [selectionSource, setSelectionSource] = useState<SelectionSource>("candidate");
  const [inspectionNote, setInspectionNote] = useState("");
  const [pageError, setPageError] = useState<string | null>(null);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [classifying, setClassifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submission, setSubmission] = useState<EquipmentDemoSubmission | null>(null);
  const [checklistStatuses, setChecklistStatuses] = useState<Record<string, MachineStatus>>({});
  const [checklistFreeTexts, setChecklistFreeTexts] = useState<Record<string, string>>({});
  const [checklistItemPhotos, setChecklistItemPhotos] = useState<Record<string, LocalChecklistItemPhoto>>({});
  const [checklistPhotoStatuses, setChecklistPhotoStatuses] = useState<Record<string, EquipmentPhotoStatus>>({});
  const [answeredChecklistItemIds, setAnsweredChecklistItemIds] = useState<string[]>([]);
  const [activeChecklistIndex, setActiveChecklistIndex] = useState(0);
  const [pendingChecklistAnswer, setPendingChecklistAnswer] = useState<MachineStatus | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [summaryEditIndex, setSummaryEditIndex] = useState<number | null>(null);
  const [llmCorrectChoice, setLlmCorrectChoice] = useState<boolean | null>(null);
  const [showAlternativeSelection, setShowAlternativeSelection] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState("");
  const [selectedDocumentGroupId, setSelectedDocumentGroupId] = useState("");
  const [selectedReferenceMaterialId, setSelectedReferenceMaterialId] = useState("");
  const [referenceModalOpen, setReferenceModalOpen] = useState(false);
  const [referenceResponses, setReferenceResponses] = useState<Record<string, Record<string, ReferenceResponse>>>({});
  const [referenceDownloadInProgress, setReferenceDownloadInProgress] = useState(false);
  const [directInputModalOpen, setDirectInputModalOpen] = useState(false);
  const [directInputDraft, setDirectInputDraft] = useState("");
  const [directInputError, setDirectInputError] = useState<string | null>(null);

  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const checklistPhotoCameraInputRef = useRef<HTMLInputElement | null>(null);
  const checklistPhotoUploadInputRef = useRef<HTMLInputElement | null>(null);
  const checklistPhotoPreviewUrlsRef = useRef<string[]>([]);

  function revokeChecklistPhotoPreviewUrl(url: string) {
    if (!url) return;
    URL.revokeObjectURL(url);
    checklistPhotoPreviewUrlsRef.current = checklistPhotoPreviewUrlsRef.current.filter((item) => item !== url);
  }

  function revokeAllChecklistPhotoPreviewUrls() {
    checklistPhotoPreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    checklistPhotoPreviewUrlsRef.current = [];
  }

  function createChecklistPhotoPreviewUrl(file: File) {
    const url = URL.createObjectURL(file);
    checklistPhotoPreviewUrlsRef.current.push(url);
    return url;
  }

  useEffect(() => {
    let mounted = true;
    loadEquipmentDemoCatalog()
      .then((result) => {
        if (!mounted) return;
        if (!result.data) {
          setPageError(result.error ?? "장비 목록을 불러오지 못했습니다.");
          return;
        }
        setCatalog(result.data);
        setPageError(result.error ?? null);
      })
      .catch((error) => {
        if (!mounted) return;
        setPageError(error instanceof Error ? error.message : String(error));
      })
      .finally(() => {
        if (mounted) setLoadingCatalog(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (selectedImagePreviewUrl) {
        URL.revokeObjectURL(selectedImagePreviewUrl);
      }
    };
  }, [selectedImagePreviewUrl]);

  useEffect(() => {
    return () => {
      revokeAllChecklistPhotoPreviewUrls();
    };
  }, []);

  const selectedChecklist = useMemo(
    () => catalog.machines.find((machine) => machine.machine_id === selectedMachineId) ?? null,
    [catalog.machines, selectedMachineId],
  );

  useEffect(() => {
    if (!selectedChecklist) {
      setChecklistStatuses({});
      setChecklistFreeTexts({});
      setChecklistItemPhotos({});
      setChecklistPhotoStatuses({});
      setAnsweredChecklistItemIds([]);
      setActiveChecklistIndex(0);
      setPendingChecklistAnswer(null);
      setPhotoError(null);
      setSelectedDocumentId("");
      setSelectedDocumentGroupId("");
      setSelectedReferenceMaterialId("");
      setReferenceModalOpen(false);
      revokeAllChecklistPhotoPreviewUrls();
      return;
    }
    setChecklistStatuses(Object.fromEntries(selectedChecklist.items.map((item) => [item.item_id, "미확인"])) as Record<string, MachineStatus>);
    setChecklistFreeTexts({});
    setChecklistItemPhotos({});
    setChecklistPhotoStatuses({});
    setAnsweredChecklistItemIds([]);
    setActiveChecklistIndex(0);
    setPendingChecklistAnswer(null);
    setPhotoError(null);
    setSummaryEditIndex(null);
    setSelectedDocumentId("");
    setSelectedDocumentGroupId("");
    setSelectedReferenceMaterialId(firstChecklistReferenceMaterialId(selectedChecklist));
    setReferenceModalOpen(false);
    revokeAllChecklistPhotoPreviewUrls();
  }, [selectedChecklist?.machine_id]);

  const currentChecklistItem: MachineChecklistItem | null = selectedChecklist?.items[activeChecklistIndex] ?? null;
  const currentChecklistStatus = currentChecklistItem ? (checklistStatuses[currentChecklistItem.item_id] ?? "미확인") : "미확인";
  const currentChecklistPhoto = currentChecklistItem ? checklistItemPhotos[currentChecklistItem.item_id] : undefined;
  const currentChecklistRequiresPhoto = Boolean(currentChecklistItem && requiresChecklistPhoto(currentChecklistItem));
  const currentChecklistAcceptsPhoto = Boolean(currentChecklistItem && acceptsChecklistPhoto(currentChecklistItem));
  const currentChecklistPhotoStatus = currentChecklistItem ? checklistPhotoStatuses[currentChecklistItem.item_id] : undefined;
  const activePhotoStatus =
    currentChecklistRequiresPhoto && currentChecklistStatus !== "미확인" && !currentChecklistPhoto
      ? pendingChecklistAnswer ?? currentChecklistStatus
      : pendingChecklistAnswer;
  const completedChecklistCount = answeredChecklistItemIds.length;
  const checklistPhotoStatusPreview = useMemo(
    () => buildChecklistPhotoStatuses(selectedChecklist, checklistItemPhotos, checklistPhotoStatuses),
    [selectedChecklist, checklistItemPhotos, checklistPhotoStatuses],
  );
  const requiredChecklistPhotoItems = selectedChecklist?.items.filter(requiresChecklistPhoto) ?? [];
  const requiredChecklistPhotoCount = requiredChecklistPhotoItems.length;
  const completedRequiredChecklistPhotoCount = requiredChecklistPhotoItems.filter((item) => {
    const status = checklistPhotoStatusPreview[item.item_id];
    return status === "submitted" || status === "submitted_late" || Boolean(checklistItemPhotos[item.item_id]);
  }).length;
  const skippedRequiredChecklistPhotoCount = requiredChecklistPhotoItems.filter((item) => checklistPhotoStatusPreview[item.item_id] === "skipped").length;
  const missingRequiredChecklistPhotoCount = requiredChecklistPhotoItems.filter((item) => checklistPhotoStatusPreview[item.item_id] === "missing").length;
  const checklistReady = Boolean(
    selectedChecklist
      && answeredChecklistItemIds.length === selectedChecklist.items.length,
  );
  const isChecklistLikeStep = currentStep === "action" || currentStep === "reference" || currentStep === "checklist" || currentStep === "summary";
  const selectedReferenceMaterials = selectedChecklist?.reference_materials || [];
  const selectedDocumentGroups = selectedChecklist?.document_groups || [];
  const selectedDocumentGroup = selectedDocumentGroups.find((group) => group.group_id === selectedDocumentGroupId) ?? null;
  const checklistDocumentGroup = selectedDocumentGroups.find((group) => group.group_id === "checklist") ?? null;
  const checklistReferenceDocuments = checklistDocumentGroup?.documents.filter(isChecklistReferenceDocument) ?? [];
  const checklistReferenceMaterials = checklistReferenceDocuments.flatMap((document) => document.materials);
  const selectedGroupReferenceDocuments = selectedDocumentGroup?.documents.filter(isChecklistReferenceDocument) ?? [];
  const selectedGroupDailyChecklistDocument = selectedDocumentGroup?.documents.find(isDailyChecklistDocument) ?? null;
  const selectedDocument = selectedDocumentGroups
    .flatMap((group) => group.documents)
    .find((document) => document.document_id === selectedDocumentId) ?? null;
  const documentViewerMaterials = selectedDocument?.materials?.length ? selectedDocument.materials : selectedReferenceMaterials;
  const activeReferenceMaterial = documentViewerMaterials.find((item) => item.material_id === selectedReferenceMaterialId) ?? documentViewerMaterials[0] ?? null;
  const activeChecklistReferenceMaterial = checklistReferenceMaterials.find((item) => item.material_id === selectedReferenceMaterialId) ?? checklistReferenceMaterials[0] ?? null;

  function resetFlow() {
    setCurrentStep("capture");
    setClassification(buildEmptyClassification());
    setDraftId("");
    setFileName("");
    setCaptureSource("camera");
    setSelectedMachineId("");
    setSelectionSource("candidate");
    setInspectionNote("");
    setSubmission(null);
    setChecklistStatuses({});
    setChecklistFreeTexts({});
    setChecklistItemPhotos({});
    setChecklistPhotoStatuses({});
    setAnsweredChecklistItemIds([]);
    setActiveChecklistIndex(0);
    setPendingChecklistAnswer(null);
    setPhotoUploading(false);
    setPhotoError(null);
    setSummaryEditIndex(null);
    setLlmCorrectChoice(null);
    setShowAlternativeSelection(false);
    setSelectedDocumentId("");
    setSelectedDocumentGroupId("");
    setSelectedReferenceMaterialId("");
    setReferenceModalOpen(false);
    setDirectInputModalOpen(false);
    setDirectInputDraft("");
    setDirectInputError(null);
    setPageError(null);
    setSelectedImagePreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return "";
    });
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    if (uploadInputRef.current) uploadInputRef.current.value = "";
    if (checklistPhotoCameraInputRef.current) checklistPhotoCameraInputRef.current.value = "";
    if (checklistPhotoUploadInputRef.current) checklistPhotoUploadInputRef.current.value = "";
    revokeAllChecklistPhotoPreviewUrls();
  }

  function setPreview(file: File | null) {
    setSelectedImagePreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return file ? URL.createObjectURL(file) : "";
    });
  }

  async function handleSelectedFile(file: File | null, source: CaptureSource) {
    if (!file) return;
    setPreview(file);
    setFileName(file.name);
    setCaptureSource(source);
    setSubmission(null);
    setSelectedMachineId("");
    setInspectionNote("");
    setReferenceModalOpen(false);
    setPageError(null);
    setClassifying(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("capture_source", source);
    const result = await classifyEquipmentDemoImage(formData, {
      distributionToken: distributionToken || undefined,
    });

    setClassifying(false);
    if (!result.data) {
      setPageError(result.error ?? "건설기계 인식에 실패했습니다.");
      return;
    }

    setDraftId(result.data.draft_id);
    setClassification(result.data.classification);
    const recommendedMachineId = result.data.classification.top_candidate?.equipment_type || result.data.classification.equipment_type || "";
    setSelectedMachineId(recommendedMachineId);
    setSelectionSource(recommendedMachineId ? "candidate" : "dropdown");
    setLlmCorrectChoice(null);
    setShowAlternativeSelection(false);
    setCurrentStep("review");
  }

  function moveToActionSelectionForMachine(machineId: string, nextSelectionSource: SelectionSource, llmCorrect: boolean | null) {
    setSelectedMachineId(machineId);
    setSelectionSource(nextSelectionSource);
    setLlmCorrectChoice(llmCorrect);
    const checklist = catalog.machines.find((machine) => machine.machine_id === machineId) ?? null;
    setSelectedReferenceMaterialId(firstChecklistReferenceMaterialId(checklist));
    setSelectedDocumentId("");
    setSelectedDocumentGroupId("");
    setCurrentStep("action");
  }

  function handleCandidateSelect(candidate: MachineClassificationCandidate) {
    setSelectedMachineId(candidate.equipment_type);
    moveToActionSelectionForMachine(candidate.equipment_type, "candidate", false);
  }

  function handleDropdownSelect(value: string) {
    setSelectedMachineId(value);
    setSelectionSource("dropdown");
    setLlmCorrectChoice(false);
  }

  function handleConfirmTopCandidate() {
    const machineId = classification.top_candidate?.equipment_type || classification.equipment_type || "";
    if (!machineId) return;
    moveToActionSelectionForMachine(machineId, "llm_confirmed", true);
  }

  function handleRejectTopCandidate() {
    setLlmCorrectChoice(false);
    setShowAlternativeSelection(true);
  }

  function goBack() {
    if (currentStep === "review") setCurrentStep("capture");
    if (currentStep === "action") setCurrentStep("review");
    if (currentStep === "reference") setCurrentStep("action");
    if (currentStep === "checklist") setCurrentStep("action");
    if (currentStep === "summary") setCurrentStep("checklist");
  }

  function openDocument(document: MachineDocumentEntry) {
    if (document.status !== "available") return;
    if (document.action === "start_checklist") {
      setCurrentStep("checklist");
      return;
    }
    if (!document.materials.length) return;
    setSelectedDocumentId(document.document_id);
    setSelectedReferenceMaterialId(document.materials[0]?.material_id || "");
    setCurrentStep("reference");
  }

  function openDocumentGroup(groupId: string) {
    const group = selectedDocumentGroups.find((item) => item.group_id === groupId);
    if (!group) return;
    const availableDocuments = group.documents.filter((document) => document.status === "available");
    if (availableDocuments.length === 1) {
      openDocument(availableDocuments[0]);
      return;
    }
    if (availableDocuments.length > 1) {
      setSelectedDocumentGroupId(group.group_id);
    }
  }

  function completeCurrentChecklistItem(item: MachineChecklistItem) {
    setAnsweredChecklistItemIds((current) => (current.includes(item.item_id) ? current : [...current, item.item_id]));
    setPendingChecklistAnswer(null);
    setPhotoError(null);

    if (summaryEditIndex !== null) {
      setSummaryEditIndex(null);
      setCurrentStep("summary");
      return;
    }

    if (!selectedChecklist || activeChecklistIndex >= selectedChecklist.items.length - 1) {
      setCurrentStep("summary");
      return;
    }
    setActiveChecklistIndex((current) => current + 1);
  }

  function handleChecklistAnswer(option: MachineStatus) {
    if (!selectedChecklist || !currentChecklistItem) return;
    setChecklistStatuses((current) => ({ ...current, [currentChecklistItem.item_id]: option }));
    if (option !== "직접기입") {
      setChecklistFreeTexts((current) => {
        const next = { ...current };
        delete next[currentChecklistItem.item_id];
        return next;
      });
    }
    if (checklistItemPhotos[currentChecklistItem.item_id]) {
      setChecklistPhotoStatuses((current) => ({ ...current, [currentChecklistItem.item_id]: "submitted" }));
    }
    if (requiresChecklistPhoto(currentChecklistItem) && !checklistItemPhotos[currentChecklistItem.item_id]) {
      setPendingChecklistAnswer(option);
      setPhotoError(null);
      return;
    }
    completeCurrentChecklistItem(currentChecklistItem);
  }

  function goPreviousChecklistItem() {
    if (!selectedChecklist || activeChecklistIndex <= 0) return;
    setSummaryEditIndex(null);
    setPendingChecklistAnswer(null);
    setPhotoError(null);
    setActiveChecklistIndex((current) => Math.max(0, current - 1));
  }

  function jumpToChecklistItem(index: number) {
    if (!selectedChecklist) return;
    if (index < 0 || index >= selectedChecklist.items.length) return;
    setSummaryEditIndex(null);
    setPendingChecklistAnswer(null);
    setPhotoError(null);
    setActiveChecklistIndex(index);
  }

  function cancelPendingPhoto() {
    if (!currentChecklistItem) return;
    setPendingChecklistAnswer(null);
    setPhotoError(null);
    setChecklistStatuses((current) => ({ ...current, [currentChecklistItem.item_id]: "미확인" }));
    setChecklistPhotoStatuses((current) => {
      const next = { ...current };
      delete next[currentChecklistItem.item_id];
      return next;
    });
    setChecklistFreeTexts((current) => {
      const next = { ...current };
      delete next[currentChecklistItem.item_id];
      return next;
    });
  }

  function completePendingPhotoWithoutUpload(status: "missing" | "skipped") {
    if (!currentChecklistItem) return;
    setChecklistPhotoStatuses((current) => ({ ...current, [currentChecklistItem.item_id]: status }));
    completeCurrentChecklistItem(currentChecklistItem);
  }

  function openDirectInputModal() {
    if (!currentChecklistItem) return;
    setDirectInputDraft(checklistFreeTexts[currentChecklistItem.item_id] ?? "");
    setDirectInputError(null);
    setDirectInputModalOpen(true);
  }

  function applyDirectInput() {
    if (!currentChecklistItem) return;
    if (!directInputDraft.trim()) {
      setDirectInputError("직접 입력 내용을 먼저 적어 주세요.");
      return;
    }
    setChecklistFreeTexts((current) => ({ ...current, [currentChecklistItem.item_id]: directInputDraft.trim() }));
    setDirectInputModalOpen(false);
    handleChecklistAnswer("직접기입");
  }

  async function handleChecklistPhotoFile(file: File | null) {
    if (!file || !draftId || !currentChecklistItem) return;
    setPhotoUploading(true);
    setPhotoError(null);
    const { file: uploadFile, metadata } = await compressChecklistImage(file);
    const formData = new FormData();
    formData.append("item_id", currentChecklistItem.item_id);
    formData.append("item_number", String(currentChecklistItem.number));
    formData.append("client_metadata", JSON.stringify(metadata));
    formData.append("file", uploadFile);

    const result = await uploadEquipmentDemoChecklistPhoto(draftId, formData);
    setPhotoUploading(false);
    if (checklistPhotoCameraInputRef.current) checklistPhotoCameraInputRef.current.value = "";
    if (checklistPhotoUploadInputRef.current) checklistPhotoUploadInputRef.current.value = "";
    if (!result.data) {
      setPhotoError(result.error ?? "체크리스트 사진 저장에 실패했습니다.");
      return;
    }

    const savedPhoto = result.data;
    const previewUrl = createChecklistPhotoPreviewUrl(uploadFile);
    const itemId = currentChecklistItem.item_id;
    setChecklistPhotoStatuses((current) => ({ ...current, [itemId]: "submitted" }));
    setChecklistItemPhotos((current) => {
      const previousPreviewUrl = current[itemId]?.preview_url;
      if (previousPreviewUrl) revokeChecklistPhotoPreviewUrl(previousPreviewUrl);
      return {
        ...current,
        [itemId]: {
          ...savedPhoto,
          preview_url: previewUrl,
        },
      };
    });
    if (pendingChecklistAnswer || currentChecklistStatus !== "미확인") {
      completeCurrentChecklistItem(currentChecklistItem);
    }
  }

  function editChecklistItem(index: number) {
    setActiveChecklistIndex(index);
    setSummaryEditIndex(index);
    setPendingChecklistAnswer(null);
    setPhotoError(null);
    setCurrentStep("checklist");
  }

  function applyReferenceResponseChange(materialId: string, rowId: string, partial: Partial<ReferenceResponse>) {
    if (!materialId) return;
    setReferenceResponses((current) => {
      const forMaterial = current[materialId] || {};
      const existing = forMaterial[rowId] || EMPTY_REFERENCE_RESPONSE;
      return {
        ...current,
        [materialId]: {
          ...forMaterial,
          [rowId]: { ...existing, ...partial },
        },
      };
    });
  }

  async function handleReferenceHwpxDownload(material: MachineReferenceMaterial | null) {
    if (!material || !material.inline_table) return;
    const forMaterial = referenceResponses[material.material_id] || {};
    const rows = material.inline_table.rows
      .map((row) => {
        const entry = forMaterial[row.row_id];
        if (!entry || (!entry.result && !entry.note)) return null;
        return {
          row_id: row.row_id,
          result: entry.result || "",
          note: entry.note || "",
        };
      })
      .filter((row): row is { row_id: string; result: string; note: string } => row !== null);
    if (!rows.length) return;

    setReferenceDownloadInProgress(true);
    try {
      const response = await fetch(
        `/api-proxy/v1/equipment-demo/reference-materials/${encodeURIComponent(material.material_id)}/filled-hwpx`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rows }),
        },
      );
      if (!response.ok) {
        const message = await response.text().catch(() => "");
        throw new Error(message || "HWPX 다운로드에 실패했습니다.");
      }
      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      let filename = `${material.title || "참고자료"}_점검결과.hwpx`;
      const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
      if (utfMatch) {
        try {
          filename = decodeURIComponent(utfMatch[1]);
        } catch {
          // keep default
        }
      } else {
        const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
        if (plainMatch) {
          filename = plainMatch[1];
        }
      }
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      setPageError(error instanceof Error ? error.message : "HWPX 다운로드에 실패했습니다.");
    } finally {
      setReferenceDownloadInProgress(false);
    }
  }

  async function handleSaveSubmission() {
    if (!draftId || !selectedChecklist || !checklistReady) return;
    setSaving(true);
    const checklistPhotosForSubmit = Object.fromEntries(
      Object.entries(checklistItemPhotos).map(([itemId, photo]) => {
        const { preview_url: _previewUrl, ...serializablePhoto } = photo;
        return [itemId, serializablePhoto];
      }),
    );
    const result = await confirmEquipmentDemoSubmission({
      draft_id: draftId,
      is_llm_correct: Boolean(llmCorrectChoice),
      final_equipment_type: selectedMachineId,
      checklist_statuses: checklistStatuses,
      checklist_free_texts: checklistFreeTexts,
      checklist_item_photos: checklistPhotosForSubmit,
      checklist_photo_statuses: checklistPhotoStatusPreview,
      inspection_note: inspectionNote,
      selection_source: selectionSource,
    });
    setSaving(false);
    if (!result.data) {
      setPageError(result.error ?? "저장에 실패했습니다.");
      return;
    }
    setSubmission(result.data);
    setPageError(result.error ?? null);
  }

  return (
    <div className="min-h-screen bg-[#eef2f5]">
      <div className="mx-auto w-full max-w-lg px-4 py-3 sm:max-w-3xl sm:px-6 lg:max-w-5xl xl:max-w-6xl">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-teal-700">건설기계 인식</p>
            <h1 className="text-xl font-black text-ink-900 sm:text-2xl">{isChecklistLikeStep ? "점검 중" : "현장 데모"}</h1>
          </div>
          <div className="flex items-center gap-2">
            {!isChecklistLikeStep ? (
              <Badge tone={pageError ? "amber" : "emerald"}>{pageError ? "오류" : "준비됨"}</Badge>
            ) : null}
            {!sharedAccess ? (
              <Button variant="secondary" className="px-3 py-2 text-sm" onClick={() => router.push("/admin")}>
                제출 관리
              </Button>
            ) : null}
            <Button variant="ghost" className="px-3 py-2 text-sm" onClick={resetFlow}>처음으로</Button>
          </div>
        </div>

        {pageError ? (
          <Card className="mt-4 border border-rose-200 bg-rose-50 p-4">
            <p className="text-sm font-semibold text-rose-900">처리 오류</p>
            <p className="mt-2 text-sm leading-7 text-rose-800">{pageError}</p>
          </Card>
        ) : null}

        {loadingCatalog ? (
          <Card className="mt-6">
            <EmptyState title="장비 목록 준비 중" description="잠시만 기다려 주세요." />
          </Card>
        ) : null}

        {!loadingCatalog ? <div className="mt-3"><StepPills current={currentStep} /></div> : null}

        {!loadingCatalog && currentStep === "capture" ? (
          <div className="mt-6 space-y-4">
            <Card className="p-6">
              <p className="text-lg font-black text-ink-900">사진을 찍어 주세요</p>
              {classifying ? (
                <div className="mt-4 rounded-2xl border border-teal-200 bg-teal-50 p-4 text-sm leading-7 text-teal-900">
                  AI를 통해 장비를 분석중입니다. 사진 속 장비 형태와 주요 구조를 확인하고 있습니다.
                </div>
              ) : null}
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <Button className="min-h-[64px] w-full text-base" onClick={() => cameraInputRef.current?.click()} disabled={classifying || catalog.machines.length === 0}>
                  {classifying && captureSource === "camera" ? "인식 중..." : "사진 촬영하기"}
                </Button>
                <Button variant="secondary" className="min-h-[64px] w-full text-base" onClick={() => uploadInputRef.current?.click()} disabled={classifying || catalog.machines.length === 0}>
                  {classifying && captureSource === "upload" ? "인식 중..." : "사진 올리기"}
                </Button>
              </div>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="sr-only"
                onChange={(event) => handleSelectedFile(event.currentTarget.files?.[0] ?? null, "camera")}
              />
              <input
                ref={uploadInputRef}
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(event) => handleSelectedFile(event.currentTarget.files?.[0] ?? null, "upload")}
              />
            </Card>

            <Card className="p-4">
              {selectedImagePreviewUrl ? (
                <>
                  <div className="overflow-hidden rounded-3xl border border-ink-200 bg-white">
                    <img
                      src={selectedImagePreviewUrl}
                      alt={fileName || "업로드한 건설기계 사진"}
                      className="h-[360px] w-full object-contain bg-white"
                    />
                  </div>
                  <p className="mt-3 text-sm text-ink-600">{fileName || "-"}</p>
                </>
              ) : (
                <EmptyState title="사진이 아직 없습니다" description="촬영하거나 업로드하면 여기에 표시됩니다." />
              )}
            </Card>
          </div>
        ) : null}

        {!loadingCatalog && currentStep === "review" ? (
          <div className="mt-6 space-y-4">
            <Card className="p-4">
              <div className="overflow-hidden rounded-3xl border border-ink-200 bg-white">
                {selectedImagePreviewUrl ? (
                  <img
                    src={selectedImagePreviewUrl}
                    alt={fileName || "업로드한 건설기계 사진"}
                    className="h-[280px] w-full object-contain bg-white"
                  />
                ) : null}
              </div>
              <div className="mt-4 flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm text-ink-500">AI 결과</p>
                  <p className="mt-1 text-xl font-black text-ink-900">{classificationStatusLabel(classification.status)}</p>
                  <p className="mt-2 text-sm leading-7 text-ink-700">{classification.reasoning_summary}</p>
                </div>
                <Badge tone={confidenceTone(classification.confidence)}>{confidenceLabel(classification.confidence)}</Badge>
              </div>
            </Card>

            {classification.status === "supported" && classification.top_candidate ? (
              <Card className="p-5">
                <p className="text-sm text-ink-500">AI가 추천한 장비</p>
                <p className="mt-2 text-2xl font-black text-ink-900">{classification.top_candidate.display_name}</p>
                <p className="mt-2 text-sm leading-7 text-ink-600">사용자가 최종 장비를 선택한 뒤 참고자료 확인과 체크리스트로 이동합니다.</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <Button className="min-h-[56px]" onClick={handleConfirmTopCandidate}>추천 장비 사용</Button>
                  <Button variant="secondary" className="min-h-[56px]" onClick={handleRejectTopCandidate}>직접 선택</Button>
                </div>
              </Card>
            ) : null}

            {((classification.status === "supported" && showAlternativeSelection) || classification.status !== "supported") ? (
              <>
                <Card className="p-4">
                  <FieldLabel>맞는 장비가 없으면 직접 선택</FieldLabel>
                  <SelectInput value={selectedMachineId} onChange={(event) => handleDropdownSelect(event.target.value)}>
                    <option value="">장비 직접 선택</option>
                    {catalog.machines.map((machine) => (
                      <option key={machine.machine_id} value={machine.machine_id}>
                        {machine.machine_name}
                      </option>
                    ))}
                  </SelectInput>
                  <div className="mt-4 flex gap-3">
                    <Button variant="secondary" onClick={goBack}>이전</Button>
                    <Button
                      onClick={() => moveToActionSelectionForMachine(selectedMachineId, "dropdown", false)}
                      disabled={!selectedMachineId}
                    >
                      다음
                    </Button>
                  </div>
                </Card>

                {classification.status === "supported" && classification.candidates.length ? (
                  <div className="space-y-3">
                    {classification.candidates.map((candidate, index) => (
                      <Card key={`${candidate.equipment_type}-${index}`} className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-lg font-black text-ink-900">{candidate.display_name}</p>
                            <p className="mt-1 text-sm text-ink-500">{candidate.equipment_type}</p>
                            <p className="mt-3 text-sm leading-7 text-ink-700">{candidate.why}</p>
                          </div>
                          <Badge tone={confidenceTone(candidate.confidence)}>{confidenceLabel(candidate.confidence)}</Badge>
                        </div>
                        <div className="mt-4">
                          <Button className="w-full" onClick={() => handleCandidateSelect(candidate)}>이 장비 선택</Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}

        {!loadingCatalog && currentStep === "action" ? (
          <div className="mt-6 space-y-4">
            {selectedChecklist ? (
              <>
                <Card className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold text-ink-500">문서 선택</p>
                      <p className="mt-1 text-2xl font-black text-ink-900">{selectedChecklist.machine_name}</p>
                      <p className="mt-2 text-sm leading-7 text-ink-600">
                        반입 전 참고자료, 일일 안전점검 시작, 표준안전매뉴얼, 고용노동부 점검표를 장비별로 확인합니다.
                      </p>
                    </div>
                    <Badge tone={selectedDocumentGroups.length ? "teal" : "ink"}>
                      문서 그룹 {selectedDocumentGroups.length}
                    </Badge>
                  </div>
                </Card>

                {selectedDocumentGroup ? (
                  selectedDocumentGroup.group_id === "checklist" ? (
                    <div className="space-y-3">
                      <Card className="p-4">
                        <div className="mb-3 flex items-start justify-between gap-3">
                          <div>
                            <p className="text-base font-black text-ink-900">반입 전 참고자료</p>
                            <p className="mt-1 text-sm leading-6 text-ink-600">반입 전 참고자료를 확인한 뒤 일일 안전점검을 시작합니다.</p>
                          </div>
                          <Badge tone="ink">
                            {selectedGroupReferenceDocuments.filter((document) => document.status === "available").length}/{selectedGroupReferenceDocuments.length}
                          </Badge>
                        </div>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {selectedGroupReferenceDocuments.map((document) => (
                            <button
                              key={document.document_id}
                              type="button"
                              onClick={() => openDocument(document)}
                              disabled={document.status !== "available"}
                              className={[
                                "min-h-[88px] rounded-2xl border px-4 py-3 text-left transition",
                                document.status === "available"
                                  ? "border-ink-200 bg-white hover:border-teal-300 hover:bg-teal-50"
                                  : "cursor-not-allowed border-amber-200 bg-amber-50 opacity-80",
                              ].join(" ")}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-black text-ink-900">{document.title}</span>
                                <Badge tone={documentStatusTone(document.status)}>{document.status_label}</Badge>
                              </div>
                              <p className="mt-2 text-sm leading-6 text-ink-600">{document.subtitle}</p>
                              {document.reason ? <p className="mt-2 text-xs font-semibold text-amber-800">{document.reason}</p> : null}
                            </button>
                          ))}
                        </div>
                      </Card>

                      {selectedGroupDailyChecklistDocument ? (
                        <button
                          type="button"
                          onClick={() => openDocument(selectedGroupDailyChecklistDocument)}
                          disabled={selectedGroupDailyChecklistDocument.status !== "available"}
                          className={[
                            "min-h-[112px] w-full rounded-3xl border p-5 text-left transition",
                            selectedGroupDailyChecklistDocument.status === "available"
                              ? "border-teal-200 bg-teal-50 hover:border-teal-300 hover:bg-teal-100"
                              : "cursor-not-allowed border-amber-200 bg-amber-50 opacity-80",
                          ].join(" ")}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-xl font-black leading-7 text-ink-900">{selectedGroupDailyChecklistDocument.title}</p>
                              <p className="mt-2 text-sm leading-6 text-ink-700">{selectedGroupDailyChecklistDocument.subtitle}</p>
                            </div>
                            <Badge tone={documentStatusTone(selectedGroupDailyChecklistDocument.status)}>
                              {selectedGroupDailyChecklistDocument.status_label}
                            </Badge>
                          </div>
                          {selectedGroupDailyChecklistDocument.reason ? (
                            <p className="mt-2 text-xs font-semibold text-amber-800">{selectedGroupDailyChecklistDocument.reason}</p>
                          ) : null}
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <Card className="p-4">
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <div>
                          <p className="text-base font-black text-ink-900">{selectedDocumentGroup.title}</p>
                          <p className="mt-1 text-sm leading-6 text-ink-600">{selectedDocumentGroup.description}</p>
                        </div>
                        <Badge tone="ink">
                          {selectedDocumentGroup.documents.filter((document) => document.status === "available").length}/{selectedDocumentGroup.documents.length}
                        </Badge>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {selectedDocumentGroup.documents.map((document) => (
                          <button
                            key={document.document_id}
                            type="button"
                            onClick={() => openDocument(document)}
                            disabled={document.status !== "available"}
                            className={[
                              "min-h-[88px] rounded-2xl border px-4 py-3 text-left transition",
                              document.status === "available"
                                ? "border-ink-200 bg-white hover:border-teal-300 hover:bg-teal-50"
                                : "cursor-not-allowed border-amber-200 bg-amber-50 opacity-80",
                            ].join(" ")}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-black text-ink-900">{document.title}</span>
                              <Badge tone={documentStatusTone(document.status)}>{document.status_label}</Badge>
                            </div>
                            <p className="mt-2 text-sm leading-6 text-ink-600">{document.subtitle}</p>
                            {document.reason ? <p className="mt-2 text-xs font-semibold text-amber-800">{document.reason}</p> : null}
                          </button>
                        ))}
                      </div>
                    </Card>
                  )
                ) : (
                  <div className="grid gap-3 sm:grid-cols-3">
                    {selectedDocumentGroups.map((group) => {
                      const availableCount = group.documents.filter((document) => document.status === "available").length;
                      return (
                        <button
                          key={group.group_id}
                          type="button"
                          onClick={() => openDocumentGroup(group.group_id)}
                          disabled={availableCount === 0}
                          className={[
                            "min-h-[132px] rounded-3xl border p-5 text-left transition",
                            availableCount > 0
                              ? "border-ink-200 bg-white hover:border-teal-300 hover:bg-teal-50"
                              : "cursor-not-allowed border-amber-200 bg-amber-50 opacity-80",
                          ].join(" ")}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-xl font-black leading-7 text-ink-900">{documentGroupButtonTitle(group.title)}</p>
                            <Badge tone={availableCount > 0 ? "teal" : "amber"}>
                              {availableCount > 0 ? "사용 가능" : "준비중"}
                            </Badge>
                          </div>
                          <p className="mt-3 text-sm leading-6 text-ink-600">{group.description}</p>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="flex">
                  <Button
                    variant="secondary"
                    onClick={selectedDocumentGroup ? () => setSelectedDocumentGroupId("") : goBack}
                  >
                    이전
                  </Button>
                </div>
              </>
            ) : (
              <EmptyState title="선택된 장비가 없습니다" description="인식 결과 확인 단계에서 장비를 먼저 확정해 주세요." />
            )}
          </div>
        ) : null}

        {!loadingCatalog && currentStep === "checklist" ? (
          <div className="mt-4 pb-40">
            {currentChecklistItem && selectedChecklist ? (
              <div className="space-y-3 sm:grid sm:grid-cols-[280px_minmax(0,1fr)] sm:items-start sm:gap-4 sm:space-y-0">
                <aside className="hidden self-start sm:sticky sm:top-4 sm:block">
                  <ChecklistOverviewTable
                    items={selectedChecklist.items}
                    activeIndex={activeChecklistIndex}
                    statuses={checklistStatuses}
                    photoStatuses={checklistPhotoStatuses}
                    onJump={jumpToChecklistItem}
                    mode="sidebar"
                  />
                </aside>
                <div className="space-y-3">
                  <div className="sm:hidden">
                    <ChecklistOverviewTable
                      items={selectedChecklist.items}
                      activeIndex={activeChecklistIndex}
                      statuses={checklistStatuses}
                      photoStatuses={checklistPhotoStatuses}
                      onJump={jumpToChecklistItem}
                      mode="mobile-collapsible"
                    />
                  </div>
                <Card className="p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-base font-black text-ink-900">{selectedChecklist.machine_name}</p>
                      <p className="mt-1 text-xs text-ink-500">
                        {selectionSource === "candidate" ? "AI 후보 선택" : "직접 선택"} · 총 {selectedChecklist.items.length}개 중 {activeChecklistIndex + 1}번째
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {checklistReferenceMaterials.length ? (
                        <Button
                          type="button"
                          variant="secondary"
                          className="px-3 py-2 text-sm"
                          onClick={() => setReferenceModalOpen(true)}
                        >
                          참고자료 확인
                        </Button>
                      ) : null}
                      <Badge tone="amber">{activeChecklistIndex + 1}/{selectedChecklist.items.length}</Badge>
                    </div>
                  </div>
                  <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-ink-100">
                    <div
                      className="h-full rounded-full bg-teal-500 transition-all"
                      style={{ width: `${((activeChecklistIndex + 1) / Math.max(selectedChecklist.items.length, 1)) * 100}%` }}
                    />
                  </div>
                </Card>

                <Card className="min-h-[42vh] p-5 sm:min-h-[38vh]">
                  <p className="text-xs font-semibold text-ink-500">체크 항목 {currentChecklistItem.number}</p>
                  <p className="mt-3 text-xl font-black leading-9 text-ink-900 sm:text-2xl sm:leading-10">{currentChecklistItem.question}</p>
                  {currentChecklistItem.result_hint || currentChecklistItem.note_hint ? (
                    <p className="mt-4 text-sm leading-7 text-ink-600">
                      {currentChecklistItem.result_hint || currentChecklistItem.note_hint}
                    </p>
                  ) : null}
                  {currentChecklistAcceptsPhoto ? (
                    <div className="mt-4 rounded-2xl border border-teal-200 bg-teal-50 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge tone={photoRequirementTone(currentChecklistItem)}>{photoRequirementLabel(currentChecklistItem)}</Badge>
                          <Badge tone={photoStatusTone(currentChecklistPhotoStatus, Boolean(currentChecklistPhoto))}>
                            {photoStatusLabel(currentChecklistPhotoStatus, Boolean(currentChecklistPhoto))}
                          </Badge>
                          {currentChecklistStatus !== "미확인" ? (
                            <Badge tone={statusTone(currentChecklistStatus)}>{statusLabel(currentChecklistStatus)}</Badge>
                          ) : null}
                        </div>
                        {currentChecklistPhoto ? <span className="text-xs font-semibold text-teal-800">{currentChecklistPhoto.filename}</span> : null}
                      </div>
                      {currentChecklistPhoto?.preview_url ? (
                        <div className="mt-3 overflow-hidden rounded-2xl border border-teal-200 bg-white">
                          <img src={currentChecklistPhoto.preview_url} alt={`${currentChecklistItem.number}번 체크리스트 제출 사진`} className="h-48 w-full object-contain" />
                        </div>
                      ) : null}
                      {!activePhotoStatus ? (
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                          <Button
                            variant="secondary"
                            className="min-h-[44px]"
                            onClick={() => checklistPhotoCameraInputRef.current?.click()}
                            disabled={photoUploading}
                          >
                            {photoUploading ? "저장 중..." : "사진 촬영"}
                          </Button>
                          <Button
                            variant="secondary"
                            className="min-h-[44px]"
                            onClick={() => checklistPhotoUploadInputRef.current?.click()}
                            disabled={photoUploading}
                          >
                            사진 올리기
                          </Button>
                        </div>
                      ) : null}
                      {currentChecklistItem.photo_review_hint ? (
                        <p className="mt-3 text-xs font-semibold leading-6 text-teal-900">{currentChecklistItem.photo_review_hint}</p>
                      ) : null}
                    </div>
                  ) : (
                    <div className="mt-4">
                      <Badge tone={photoRequirementTone(currentChecklistItem)}>{photoRequirementLabel(currentChecklistItem)}</Badge>
                    </div>
                  )}
                  {exampleImagesForItem(currentChecklistItem).length ? (
                    <div className="mt-5 grid gap-3 sm:grid-cols-2">
                      {exampleImagesForItem(currentChecklistItem).map((image) => (
                        <div key={image.example_id} className="overflow-hidden rounded-3xl border border-ink-200 bg-white">
                          <div className="flex items-center justify-between gap-3 border-b border-ink-100 px-4 py-3">
                            <Badge tone={exampleImageTone(image.kind)}>{image.label}</Badge>
                            <span className="text-xs font-semibold text-ink-500">예시 사진</span>
                          </div>
                          <img
                            src={`/api-proxy/v1/equipment-demo/checklist-example-images/${encodeURIComponent(image.example_id)}`}
                            alt={image.alt}
                            className="h-56 w-full bg-ink-50 object-cover"
                          />
                          {image.caption ? (
                            <p className="px-4 py-3 text-sm leading-7 text-ink-600">{image.caption}</p>
                          ) : null}
                          {isAiGeneratedExample(image) ? (
                            <div className="px-4 pb-4">
                              <AiAssistNotice notice={AI_ASSIST_NOTICES.ai_generated_example_photo} compact />
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </Card>

                <div className="fixed inset-x-0 bottom-0 z-30 px-4 pb-4">
                  <div className="mx-auto w-full max-w-lg sm:max-w-3xl lg:max-w-5xl xl:max-w-6xl">
                    <Card className="border border-ink-200 bg-white/95 p-4 shadow-soft backdrop-blur">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <Button
                          type="button"
                          variant="secondary"
                          className="px-3 py-2 text-sm"
                          onClick={goPreviousChecklistItem}
                          disabled={activeChecklistIndex <= 0 || photoUploading}
                        >
                          이전 항목
                        </Button>
                        <Badge tone="ink">{completedChecklistCount}/{selectedChecklist.items.length}</Badge>
                      </div>

                      {activePhotoStatus ? (
                        <div className="space-y-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge tone={statusTone(activePhotoStatus)}>{statusLabel(activePhotoStatus)}</Badge>
                            <span className="text-sm font-semibold text-ink-700">항목 사진 제출</span>
                          </div>
                          {photoError ? <p className="rounded-2xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-800">{photoError}</p> : null}
                          <div className="grid gap-2 sm:grid-cols-2">
                            <Button
                              className="min-h-[56px] text-base"
                              onClick={() => checklistPhotoCameraInputRef.current?.click()}
                              disabled={photoUploading}
                            >
                              {photoUploading ? "저장 중..." : "사진 촬영"}
                            </Button>
                            <Button
                              variant="secondary"
                              className="min-h-[56px] text-base"
                              onClick={() => checklistPhotoUploadInputRef.current?.click()}
                              disabled={photoUploading}
                            >
                              사진 올리기
                            </Button>
                          </div>
                          <div className="grid gap-2 sm:grid-cols-3">
                            <Button
                              variant="secondary"
                              className="min-h-[48px] text-base"
                              onClick={() => completePendingPhotoWithoutUpload("missing")}
                              disabled={photoUploading}
                            >
                              사진 없이 다음
                            </Button>
                            <Button
                              variant="secondary"
                              className="min-h-[48px] text-base"
                              onClick={() => completePendingPhotoWithoutUpload("skipped")}
                              disabled={photoUploading}
                            >
                              건너뛰기
                            </Button>
                            <Button
                              variant="ghost"
                              className="min-h-[48px] text-base"
                              onClick={cancelPendingPhoto}
                              disabled={photoUploading}
                            >
                              다시 선택
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <ChecklistActionPanel
                          onAccept={() => handleChecklistAnswer("적합")}
                          onReject={() => handleChecklistAnswer("부적합")}
                          onDirectInput={openDirectInputModal}
                        />
                      )}
                      <input
                        ref={checklistPhotoCameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="sr-only"
                        onChange={(event) => handleChecklistPhotoFile(event.currentTarget.files?.[0] ?? null)}
                      />
                      <input
                        ref={checklistPhotoUploadInputRef}
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={(event) => handleChecklistPhotoFile(event.currentTarget.files?.[0] ?? null)}
                      />
                    </Card>
                  </div>
                </div>
                </div>
              </div>
            ) : (
              <EmptyState title="체크리스트를 불러올 수 없습니다" description="인식 단계에서 장비를 선택한 뒤 다시 진행해 주세요." />
            )}
          </div>
        ) : null}

        {!loadingCatalog && currentStep === "reference" ? (
          <div className="mt-4 space-y-3 pb-40">
            {selectedChecklist ? (
              <>
                <Card className="p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold text-ink-500">참고자료 확인</p>
                      <p className="mt-1 text-lg font-black text-ink-900">{selectedDocument?.title || selectedChecklist.machine_name}</p>
                      <p className="mt-2 text-sm leading-7 text-ink-600">
                        {selectedDocument?.subtitle || "사용자 확인 후 체크리스트로 넘어가기 전에 관련 참고자료를 먼저 확인합니다."}
                      </p>
                    </div>
                    <Badge tone="teal">{documentViewerMaterials.length}개 자료</Badge>
                  </div>
                </Card>

                <Card className="p-5">
                  <ReferenceMaterialViewer
                    materials={documentViewerMaterials}
                    activeMaterial={activeReferenceMaterial}
                    onSelectMaterial={setSelectedReferenceMaterialId}
                    responses={referenceResponses[activeReferenceMaterial?.material_id || ""] || {}}
                    onResponseChange={(rowId, partial) =>
                      applyReferenceResponseChange(activeReferenceMaterial?.material_id || "", rowId, partial)
                    }
                    onDownload={() => handleReferenceHwpxDownload(activeReferenceMaterial)}
                    downloadInProgress={referenceDownloadInProgress}
                  />
                  <div className="mt-5 flex flex-wrap gap-3">
                    <Button type="button" variant="secondary" onClick={goBack}>
                      이전
                    </Button>
                    <Button type="button" onClick={() => setCurrentStep("checklist")}>
                      체크리스트로 이동
                    </Button>
                  </div>
                </Card>
              </>
            ) : (
              <EmptyState title="참고자료를 찾지 못했습니다" description="장비를 다시 선택한 뒤 진행해 주세요." />
            )}
          </div>
        ) : null}

        {!loadingCatalog && currentStep === "summary" ? (
          <div className="mt-4 space-y-3 pb-40">
            <Card className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold text-ink-500">점검 요약</p>
                  <p className="mt-1 text-lg font-black text-ink-900">{selectedChecklist?.machine_name || "-"}</p>
                  <p className="mt-2 text-sm leading-7 text-ink-600">필요한 항목만 수정하고 바로 제출하세요.</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Badge tone="emerald">{completedChecklistCount}/{selectedChecklist?.items.length || 0}</Badge>
                  <Badge tone={missingRequiredChecklistPhotoCount || skippedRequiredChecklistPhotoCount ? "amber" : "teal"}>
                    사진 {completedRequiredChecklistPhotoCount}/{requiredChecklistPhotoCount}
                  </Badge>
                </div>
              </div>
              {missingRequiredChecklistPhotoCount || skippedRequiredChecklistPhotoCount ? (
                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold leading-6 text-amber-900">
                  미제출 {missingRequiredChecklistPhotoCount}건, 건너뛰기 {skippedRequiredChecklistPhotoCount}건이 제출 기록과 HWPX에 함께 남습니다.
                </div>
              ) : null}
              <AiAssistNotice notice={AI_ASSIST_NOTICES.equipment_photo_review} compact />
              <div className="mt-4">
                <FieldLabel>메모</FieldLabel>
                <TextArea value={inspectionNote} onChange={(event) => setInspectionNote(event.target.value)} placeholder="현장 메모" />
              </div>
            </Card>

            {submission ? (
              <Card className="p-6">
                <p className="text-sm font-semibold text-emerald-700">저장 완료</p>
                <p className="mt-2 text-2xl font-black text-ink-900">{submission.checklist.machine_name}</p>
                <p className="mt-3 text-sm leading-7 text-ink-700">사진, AI 결과, 최종 선택 장비, 체크리스트 상태가 저장되었습니다.</p>
                <div className="mt-4 space-y-1 text-sm text-ink-600">
                  <p>제출 번호: {submission.submission_id}</p>
                  <p>저장 시각: {submission.submitted_at}</p>
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    onClick={() =>
                      window.open(
                        `/api-proxy/v1/equipment-demo/submissions/${encodeURIComponent(submission.submission_id)}/checklist-file`,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                  >
                    점검표 다운로드
                  </Button>
                  {!sharedAccess ? (
                    <>
                      <Button onClick={() => router.push(`/admin/${submission.submission_id}`)}>
                        방금 제출 상세 보기
                      </Button>
                      <Button variant="secondary" onClick={() => router.push("/admin")}>
                        제출 목록 보기
                      </Button>
                    </>
                  ) : null}
                  <Button variant="secondary" onClick={resetFlow}>다시 시작</Button>
                </div>
              </Card>
            ) : selectedChecklist ? (
              <div className="space-y-3">
                {selectedChecklist.items.map((item, index) => (
                  <Card key={item.item_id} className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge tone={statusTone(checklistStatuses[item.item_id] ?? "미확인")}>
                            {statusLabel(checklistStatuses[item.item_id] ?? "미확인")}
                          </Badge>
                          <span className="text-xs font-semibold text-ink-500">항목 {item.number}</span>
                        </div>
                        <p className="mt-2 text-base font-semibold leading-7 text-ink-900">{item.question}</p>
                        {checklistStatuses[item.item_id] === "직접기입" && checklistFreeTexts[item.item_id] ? (
                          <p className="mt-2 text-sm leading-7 text-ink-700">직접기입: {checklistFreeTexts[item.item_id]}</p>
                        ) : null}
                        {acceptsChecklistPhoto(item) ? (
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <Badge tone={photoRequirementTone(item)}>{photoRequirementLabel(item)}</Badge>
                            <Badge tone={photoStatusTone(checklistPhotoStatusPreview[item.item_id], Boolean(checklistItemPhotos[item.item_id]))}>
                              {photoStatusLabel(checklistPhotoStatusPreview[item.item_id], Boolean(checklistItemPhotos[item.item_id]))}
                            </Badge>
                            {checklistItemPhotos[item.item_id]?.filename ? (
                              <span className="text-xs font-semibold text-ink-500">{checklistItemPhotos[item.item_id].filename}</span>
                            ) : null}
                          </div>
                        ) : (
                          <div className="mt-3">
                            <Badge tone={photoRequirementTone(item)}>{photoRequirementLabel(item)}</Badge>
                          </div>
                        )}
                      </div>
                      <Button variant="secondary" className="shrink-0" onClick={() => editChecklistItem(index)}>
                        수정
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            ) : null}

            {!submission ? (
              <div className="fixed inset-x-0 bottom-0 z-30 px-4 pb-4">
                <div className="mx-auto w-full max-w-lg sm:max-w-3xl lg:max-w-5xl xl:max-w-6xl">
                  <Card className="border border-ink-200 bg-white/95 p-4 shadow-soft backdrop-blur">
                    <div className="flex items-center justify-between gap-3">
                      <Button variant="secondary" onClick={goBack}>이전</Button>
                      <Button onClick={handleSaveSubmission} disabled={!checklistReady || saving}>
                        {saving ? "제출 중..." : "제출하기"}
                      </Button>
                    </div>
                  </Card>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <Modal open={referenceModalOpen} title="참고자료 확인" onClose={() => setReferenceModalOpen(false)}>
        <div className="space-y-4">
          <div className="rounded-3xl bg-ink-50 p-4">
            <p className="text-sm font-semibold text-ink-900">{selectedChecklist?.machine_name || "-"}</p>
            <p className="mt-1 text-sm leading-7 text-ink-600">반입 전 참고자료를 확인하고 직접 점검 결과를 기록할 수 있습니다.</p>
          </div>
          <ReferenceMaterialViewer
            materials={checklistReferenceMaterials}
            activeMaterial={activeChecklistReferenceMaterial}
            onSelectMaterial={setSelectedReferenceMaterialId}
            responses={referenceResponses[activeChecklistReferenceMaterial?.material_id || ""] || {}}
            onResponseChange={(rowId, partial) =>
              applyReferenceResponseChange(activeChecklistReferenceMaterial?.material_id || "", rowId, partial)
            }
            onDownload={() => handleReferenceHwpxDownload(activeChecklistReferenceMaterial)}
            downloadInProgress={referenceDownloadInProgress}
          />
        </div>
      </Modal>

      <Modal open={directInputModalOpen} title="직접기입" onClose={() => setDirectInputModalOpen(false)}>
        <div className="space-y-4">
          <div className="rounded-3xl bg-ink-50 p-4 text-sm leading-7 text-ink-700">
            <p className="font-semibold text-ink-900">{currentChecklistItem?.question || "-"}</p>
            <p className="mt-2">직접 입력한 내용은 점검결과에 `기타`로 반영되고, 상세 내용은 HWPX의 `점검자 의견`란에 기록됩니다.</p>
          </div>
          <div>
            <FieldLabel>직접 입력 내용</FieldLabel>
            <TextArea
              value={directInputDraft}
              onChange={(event) => setDirectInputDraft(event.target.value)}
              placeholder="예: 안전핀 일부 마모 확인되어 교체 필요"
            />
            {directInputError ? <p className="mt-2 text-sm text-rose-700">{directInputError}</p> : null}
          </div>
          <div className="flex flex-wrap gap-3">
            <Button type="button" variant="secondary" onClick={() => setDirectInputModalOpen(false)}>
              취소
            </Button>
            <Button type="button" onClick={applyDirectInput}>
              저장
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
